import type { DatesSetArg } from '@fullcalendar/core'
import deLocale from '@fullcalendar/core/locales/de'
import enLocale from '@fullcalendar/core/locales/en-gb'
import dayGridPlugin from '@fullcalendar/daygrid'
import interactionPlugin from '@fullcalendar/interaction'
import listPlugin from '@fullcalendar/list'
import timeGridPlugin from '@fullcalendar/timegrid'
import CloseIcon from '@mui/icons-material/Close'
import ListIcon from '@mui/icons-material/List'
import { IconButton, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import Tooltip, { type TooltipProps } from '@mui/material/Tooltip'
import { useCallback } from 'react'
import { Button, CreateButton, List, TopToolbar, useLocale, useTranslate } from 'react-admin'
import { useDispatch, useSelector } from 'react-redux'
import { makeStyles } from 'tss-react/mui'
import { Calendar, LinkToRecord } from '~/components'
import type { CalendarProps } from '~/components/molecules/Calendar/Calendar'
import { changeDates, changeView } from '~/reducers'
import { AppState, CalendarViewName } from '~/types'
import { getFilterValuesFromInterval } from '~/vendor/ra-calendar'

const middleDate = (start: Date | string | number, end: Date | string | number): Date => {
  const startDate = start instanceof Date ? start : new Date(start)
  const endDate = end instanceof Date ? end : new Date(end)
  return new Date((startDate.getTime() + endDate.getTime()) / 2)
}

const useStyles = makeStyles()((theme) => ({
  multiline: {
    whiteSpace: 'pre-line',
  },
}))

const AppointmentCalendarViewActions = ({ setShowCalendar }) => (
  <TopToolbar>
    <CreateButton />
    <Button onClick={() => setShowCalendar(false)} label='resources.appointments.list_view'>
      <ListIcon />
    </Button>
  </TopToolbar>
)

const HtmlTooltip = styled(({ className, ...props }: TooltipProps) => (
  <Tooltip {...props} classes={{ popper: className }} />
))(({ theme }) => ({
  [`& .MuiTooltip-tooltip`]: {
    backgroundColor: theme.palette.background.default, //'#fcfcfe',
    color: theme.palette.text.primary, //'rgba(0, 0, 0, 0.87)',
    maxWidth: 300,
    minWidth: 150,
    fontSize: theme.typography.pxToRem(12),
    border: '1px solid #dadde9',
  },
}))

const NoTransition = ({ children }) => children

const HOUR = 60 * 60 * 1000

/**
 * An appointment as a calendar event. FullCalendar makes a title of String(),
 * so a missing one (every viewing) became the text "null" — and went back to
 * the database as that on the next drag.
 */
export const recordToEvent = (record) => ({ ...record, title: record.title ?? '' })

/**
 * A dragged or resized event back into the appointment's row: its own fields
 * and the new times, each an instant with its offset. FullCalendar gives an
 * event no end when it ends as it starts; the save then had none, and the
 * database refused it ("null value in column end") — such an event gets an
 * hour (a day when all-day).
 */
export const calendarChangeToRecord = (event) => {
  const props = event.extendedProps ?? {}
  const start = new Date(event.start)
  const end = event.end
    ? new Date(event.end)
    : new Date(start.getTime() + (event.allDay ? 24 * HOUR : HOUR))
  return {
    id: event.id,
    uid: props.uid,
    type: props.type ?? null,
    title: event.title || null,
    location: props.location ?? null,
    notes: props.notes ?? null,
    allDay: !!event.allDay,
    start: start.toISOString(),
    end: end.toISOString(),
  }
}

const AppointmentCalendarView = ({ setShowCalendar, types, loadingTypes, ...rest }) => {
  const locale = useLocale()
  const translate = useTranslate()
  const view = useSelector((state: AppState) => state.calendar.view)
  const dates = useSelector((state: AppState) => state.calendar.dates)
  const initialDate = dates.start && dates.end ? middleDate(dates.start, dates.end) : undefined
  const dispatch = useDispatch()

  const { classes } = useStyles()

  const handleTransform = calendarChangeToRecord

  const translateType = (type) =>
    types ? types.choices.find((c) => c.key === type).translated_name || type : type

  const calendarProps: CalendarProps = {
    locales: [enLocale, deLocale],
    locale: locale,
    plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin],
    headerToolbar: {
      left: 'prev,next today',
      center: 'title',
      right: 'dayGridMonth,timeGridWeek,timeGridDay,listMonth',
    },
    eventContentExtend: (eventInfo, renderInner) => {
      const {
        id,
        title,
        start,
        end,
        extendedProps: {
          uid,
          type,
          appointment_boat,
          appointment_customer,
          appointment_user,
          location,
          notes,
        },
      } = eventInfo.event
      const { isSelected, unselect } = eventInfo
      const openProps = isSelected
        ? { open: true, interactive: true, TransitionComponent: NoTransition }
        : {}
      return (
        <HtmlTooltip
          {...openProps}
          arrow
          title={
            <>
              {eventInfo.isSelected && (
                <IconButton
                  aria-label='close'
                  style={{ float: 'right' }}
                  size='small'
                  onClick={unselect}
                >
                  <CloseIcon />
                </IconButton>
              )}
              <Typography color='inherit'>{type ? translateType(type) : title}</Typography>
              {location}
              {appointment_boat?.map(
                ({ boat_id, boat }) =>
                  boat && (
                    <div key={boat_id}>
                      <LinkToRecord resource='boats' record={{ id: boat_id }}>
                        <>
                          {boat.model} ({boat.name})
                        </>
                      </LinkToRecord>
                    </div>
                  )
              )}
              {appointment_customer?.map(
                ({ customer_id, customer }) =>
                  customer && (
                    <div key={customer_id}>
                      <LinkToRecord resource='customers' record={{ id: customer_id }}>
                        <>
                          {customer.informal_salutation ? (
                            <>
                              <b>{customer.firstname}</b> {customer.lastname}
                            </>
                          ) : (
                            <>
                              {customer.firstname} <b>{customer.lastname}</b>
                            </>
                          )}
                        </>
                      </LinkToRecord>
                      {customer.phones &&
                        customer.phones.map(({ id, type, number }) => (
                          <div key={id}>
                            {number} ({type})
                          </div>
                        ))}
                      {customer.emails &&
                        customer.emails.map(({ id, type, email }) => (
                          <div key={id}>
                            {email} ({type})
                          </div>
                        ))}
                    </div>
                  )
              )}
              {appointment_user?.length > 0 && (
                <div>
                  {translate('resources.appointments.fields.appointment_user')}:{' '}
                  {appointment_user
                    .map(({ user }) => user?.displayName || user?.email)
                    .filter(Boolean)
                    .join(', ')}
                </div>
              )}
              <div className={classes.multiline}>{notes}</div>
            </>
          }
        >
          {renderInner({
            ...eventInfo,
            titleOverride: type ? `${translateType(type)}` : `${title}`,
          })}
        </HtmlTooltip>
      )
    },
    convertToEvent: recordToEvent,
    initialView: view,
    initialDate: initialDate,
    datesSetExtend: useCallback(
      (dateInfo: DatesSetArg) => {
        if (!(dates.start === dateInfo.startStr && dates.end === dateInfo.endStr)) {
          dispatch(changeDates(dateInfo.startStr, dateInfo.endStr))
        }
        if (view !== dateInfo.view.type) {
          // FullCalendar types view.type as a plain string; the four views this
          // calendar registers are exactly CalendarViewName.
          dispatch(changeView(dateInfo.view.type as CalendarViewName))
        }
      },
      [dispatch, view, dates.start, dates.end]
    ),
  }

  return (
    <>
      <List
        title='resources.appointments.title'
        filterDefaultValues={getFilterValuesFromInterval()}
        perPage={1000}
        actions={<AppointmentCalendarViewActions setShowCalendar={setShowCalendar} />}
        {...rest}
        pagination={false}
      >
        <Calendar transform={handleTransform} {...calendarProps} />
      </List>
    </>
  )
}

export default AppointmentCalendarView
